import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  CarOutlined,
  FileTextOutlined,
  SettingOutlined,
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
  DollarOutlined,
  ToolOutlined,
  LogoutOutlined,
  TeamOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  MessageOutlined,
  RobotOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  TagsOutlined,
  ExperimentOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import { Button, Input, Badge, Avatar, Dropdown } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationCounts } from '../../services/notificationApi';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  badge?: number;
  children?: MenuItem[];
}

const getMenuItems = (userRole?: string, counts?: NotificationCounts): MenuItem[] => {
  const rolePrefix = userRole === 'customer' ? '/portal' :
    userRole === 'technician' ? '/technician' :
      userRole === 'advisor' ? '/advisor' :
        userRole === 'owner' ? '/owner' : '/admin';

  const baseItems: MenuItem[] = [
    { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard', path: `${rolePrefix}/dashboard` },
  ];

  if (userRole === 'owner') {
    return [
      ...baseItems,
      { key: 'users', icon: <TeamOutlined />, label: 'User Management', path: `${rolePrefix}/users` },
      { key: 'customers', icon: <UserOutlined />, label: 'Customers', path: `${rolePrefix}/customers` },
      { key: 'appointments', icon: <CalendarOutlined />, label: 'Appointments', path: `${rolePrefix}/appointments`, badge: counts?.appointment || undefined },
      { key: 'estimates', icon: <DollarOutlined />, label: 'Estimates', path: `${rolePrefix}/estimates`, badge: counts?.estimate || undefined },
      { key: 'work-orders', icon: <ToolOutlined />, label: 'Work Orders', path: `${rolePrefix}/work-orders`, badge: counts?.work_order || undefined },
      { key: 'estimate-templates', icon: <AppstoreOutlined />, label: 'Estimate Templates', path: `${rolePrefix}/estimate-templates` },
      { key: 'quote-requests', icon: <MessageOutlined />, label: 'Quote Requests', path: `${rolePrefix}/quote-requests` },
      { key: 'templates', icon: <FileTextOutlined />, label: 'Templates', path: `${rolePrefix}/templates` },
      { key: 'reports', icon: <RobotOutlined />, label: 'Intelligence Hub', path: `${rolePrefix}/reports` },
      { key: 'knowledge-base', icon: <DatabaseOutlined />, label: 'Knowledge Base', path: `${rolePrefix}/knowledge-base` },
      { key: 'shared-parts', icon: <SearchOutlined />, label: 'Parts Browser', path: `${rolePrefix}/shared-parts` },
      { key: 'labor-times', icon: <ClockCircleOutlined />, label: 'Labor Times', path: `${rolePrefix}/labor-times` },
      { key: 'service-categories', icon: <TagsOutlined />, label: 'Service Categories', path: `${rolePrefix}/service-categories` },
      { key: 'operation-seeder', icon: <ExperimentOutlined />, label: 'Operation Seeder', path: `${rolePrefix}/operation-seeder` },
      { key: 'work-orders-archived', icon: <OrderedListOutlined />, label: 'Archived Work Orders', path: `${rolePrefix}/work-orders/archived` },
      { key: 'settings', icon: <SettingOutlined />, label: 'Settings', path: `${rolePrefix}/settings` },
    ];
  }

  if (userRole === 'advisor') {
    return [
      ...baseItems,
      { key: 'customers', icon: <UserOutlined />, label: 'Customers', path: `${rolePrefix}/customers` },
      { key: 'appointments', icon: <CalendarOutlined />, label: 'Appointments', path: `${rolePrefix}/appointments` },
      { key: 'estimates', icon: <DollarOutlined />, label: 'Estimates', path: `${rolePrefix}/estimates`, badge: counts?.estimate || undefined },
      { key: 'work-orders', icon: <ToolOutlined />, label: 'Work Orders', path: `${rolePrefix}/work-orders`, badge: counts?.work_order || undefined },
      { key: 'estimate-templates', icon: <AppstoreOutlined />, label: 'Estimate Templates', path: `${rolePrefix}/estimate-templates` },
      { key: 'quote-requests', icon: <MessageOutlined />, label: 'Quote Requests', path: `${rolePrefix}/quote-requests` },
      { key: 'shared-parts', icon: <SearchOutlined />, label: 'Parts Browser', path: `${rolePrefix}/shared-parts` },
      { key: 'templates', icon: <FileTextOutlined />, label: 'Templates', path: `${rolePrefix}/templates` },
    ];
  }

  if (userRole === 'technician') {
    return [
      ...baseItems,
      { key: 'work-orders', icon: <ToolOutlined />, label: 'Work Orders', path: `${rolePrefix}/work-orders`, badge: counts?.work_order || undefined },
      { key: 'schedule', icon: <CalendarOutlined />, label: 'My Schedule', path: `${rolePrefix}/schedule` },
    ];
  }

  if (userRole === 'customer') {
    return [
      ...baseItems,
      { key: 'estimates', icon: <DollarOutlined />, label: 'My Estimates', path: `${rolePrefix}/estimates` },
      { key: 'vehicles', icon: <CarOutlined />, label: 'My Vehicles', path: `${rolePrefix}/vehicles` },
      { key: 'history', icon: <FileTextOutlined />, label: 'Service History', path: `${rolePrefix}/history` },
      { key: 'payments', icon: <SettingOutlined />, label: 'Payments', path: `${rolePrefix}/payments` },
    ];
  }

  return baseItems;
};

const getQuickActions = (userRole?: string) => {
  const rolePrefix = userRole === 'customer' ? '/portal' :
    userRole === 'technician' ? '/technician' :
      userRole === 'advisor' ? '/advisor' :
        userRole === 'owner' ? '/owner' : '/admin';

  if (userRole === 'owner' || userRole === 'advisor') {
    return [
      { key: 'new-customer', label: 'New Customer', path: `${rolePrefix}/customers/new` },
      { key: 'new-estimate', label: 'New Estimate', path: `${rolePrefix}/estimates/new` },
      { key: 'new-work-order', label: 'New Work Order', path: `${rolePrefix}/work-orders/new` },
    ];
  }
  if (userRole === 'technician') {
    return [
      { key: 'clock-in', label: 'Clock In/Out', path: `${rolePrefix}/clock-in` },
      { key: 'request-parts', label: 'Request Parts', path: `${rolePrefix}/parts-request` },
    ];
  }
  if (userRole === 'customer') {
    return [
      { key: 'schedule', label: 'Schedule Appointment', path: `${rolePrefix}/appointments` },
      { key: 'payment', label: 'Make Payment', path: `${rolePrefix}/payments` },
    ];
  }
  return [];
};

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCounts } = useNotifications();
  const [searchValue, setSearchValue] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Track screen size reactively
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = getMenuItems(user?.role, unreadCounts);
  const quickActions = getQuickActions(user?.role);

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setTimeout(() => onCollapse(true), 150);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenuItemsArray = [
    { key: 'profile', label: 'Profile', icon: <UserOutlined /> },
    {
      key: 'settings', label: 'Settings', icon: <SettingOutlined />,
      onClick: () => { navigate('/admin/settings'); if (isMobile) setTimeout(() => onCollapse(true), 150); },
    },
    { type: 'divider' as const },
    { key: 'logout', label: 'Sign Out', icon: <LogoutOutlined />, danger: true, onClick: handleLogout },
  ];

  const quickActionMenuItems = quickActions.map(action => ({
    key: action.key,
    label: action.label,
    onClick: () => { navigate(action.path); if (isMobile) setTimeout(() => onCollapse(true), 150); },
  }));

  // On mobile: sidebar is an overlay drawer. On desktop: fixed sidebar.
  const isOpen = !collapsed;
  const showMobileOverlay = isMobile && isOpen;

  // On mobile when collapsed: hide completely (off-screen)
  // On mobile when open: full-width overlay at 280px
  // On desktop when collapsed: 80px thin bar
  // On desktop when open: 280px
  const sidebarLeft = isMobile ? (collapsed ? '-300px' : '0px') : '0px';
  const sidebarWidth = isMobile ? '280px' : (collapsed ? '80px' : '280px');

  return (
    <>
      {/* Mobile Overlay Backdrop */}
      {showMobileOverlay && (
        <div
          onClick={() => onCollapse(true)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            WebkitTapHighlightColor: 'transparent',
          }}
        />
      )}

      {/* Sidebar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: sidebarLeft,
          height: '100vh',
          width: sidebarWidth,
          backgroundColor: 'white',
          borderRight: '1px solid #e5e7eb',
          zIndex: 999,
          transition: 'left 0.3s ease, width 0.3s ease',
          boxShadow: isOpen ? '2px 0 12px rgba(0, 0, 0, 0.15)' : '1px 0 4px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: collapsed && !isMobile ? '0 16px' : '0 16px',
          borderBottom: '1px solid #f3f4f6',
          flexShrink: 0,
        }}>
          {(!collapsed || isMobile) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CarOutlined style={{ color: 'white', fontSize: '18px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#111827', lineHeight: 1 }}>CNF Auto</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>Shop Management</div>
              </div>
            </div>
          )}
          {collapsed && !isMobile && (
            <div style={{
              width: '36px', height: '36px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto',
            }}>
              <CarOutlined style={{ color: 'white', fontSize: '18px' }} />
            </div>
          )}

          {/* Close button on mobile, collapse toggle on desktop */}
          {isMobile && isOpen && (
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: '18px' }} />}
              onClick={() => onCollapse(true)}
              style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            />
          )}
        </div>

        {/* Quick Actions */}
        {!collapsed && quickActions.length > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <Dropdown menu={{ items: quickActionMenuItems }} trigger={['click']} placement="bottomLeft">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                block
                style={{
                  height: '44px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                }}
              >
                Quick Actions
              </Button>
            </Dropdown>
          </div>
        )}

        {/* Search */}
        {!collapsed && user?.role !== 'customer' && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ borderRadius: '8px', height: '38px' }}
            />
          </div>
        )}

        {/* Navigation Menu — scrollable */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: collapsed && !isMobile ? '0 10px' : '0 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));

              return (
                <li key={item.key}>
                  <button
                    onClick={() => handleMenuClick(item.path)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: collapsed && !isMobile ? '0' : '12px',
                      padding: collapsed && !isMobile ? '10px' : '10px 12px',
                      borderRadius: '8px',
                      textAlign: 'left',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                      background: isActive ? '#eff6ff' : 'transparent',
                      color: isActive ? '#2563eb' : '#374151',
                      fontWeight: isActive ? '600' : '500',
                      fontSize: '14px',
                      minHeight: '42px',
                    }}
                  >
                    <span style={{
                      fontSize: '18px',
                      color: isActive ? '#2563eb' : '#6b7280',
                      flexShrink: 0,
                      width: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </span>

                    {(collapsed && !isMobile) ? null : (
                      <>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </span>
                        {item.badge && (
                          <Badge
                            count={item.badge}
                            size="small"
                            style={{
                              backgroundColor: isActive ? '#2563eb' : '#ef4444',
                              fontSize: '10px',
                            }}
                          />
                        )}
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Profile */}
        <div style={{
          borderTop: '1px solid #f3f4f6',
          padding: collapsed && !isMobile ? '10px' : '10px 16px',
          flexShrink: 0,
        }}>
          <Dropdown menu={{ items: userMenuItemsArray }} trigger={['click']} placement="topLeft">
            <button style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
              gap: '10px',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              border: '1px solid transparent',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
              minHeight: '48px',
            }}>
              <Avatar
                size={32}
                icon={<UserOutlined />}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  flexShrink: 0,
                }}
              />
              {(collapsed && !isMobile) ? null : (
                <div style={{ flex: 1, textAlign: 'left', overflow: 'hidden' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                  </div>
                </div>
              )}
            </button>
          </Dropdown>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
